import { Component, computed, input, InputSignal, OnChanges, OnDestroy, output, OutputEmitterRef, Signal, SimpleChanges, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { CssStyling, getSprayTypeStyling, getSprayTypeTranslation } from '../../../../blight-v2/shared/helpers/spray.helpers';
import { DateService } from '../../../services/date.service';
import { CropFieldAdvice, DailyAdvice, SprayAdvice } from '../../disease-overview-table.component/models/models';

@Component({
  template: '',
  standalone: true,
})
export class TableRowComponent implements OnDestroy, OnChanges {
  private readonly dateService = inject(DateService);

  public readonly selectedDates: InputSignal<Date[]> = input.required<Date[]>();
  public readonly advice: InputSignal<CropFieldAdvice> = input.required<CropFieldAdvice>();
  public readonly isLastRow: InputSignal<boolean> = input<boolean>(false);
  public readonly translationPrefix: InputSignal<string> = input<string>('blightV2');

  public readonly selectionChange: OutputEmitterRef<boolean> = output<boolean>();

  protected readonly cropFieldName: Signal<string> = computed<string>(() => this.advice().cropFieldName);
  protected readonly amountOfHectares: Signal<number> = computed<number>(() => this.advice().amountOfHectares);
  protected readonly dailyAdvices: Signal<DailyAdvice[]> = computed<DailyAdvice[]>(() => this.getAdvices(this.selectedDates(), this.advice()));

  protected readonly isSelected$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  protected readonly selected: InputSignal<boolean> = input<boolean>(false);

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.selected) {
      this.isSelected$.next(this.selected());
    }
  }

  public ngOnDestroy(): void {
    this.isSelected$.complete();
  }

  protected onSelectionChange(isSelected: boolean): void {
    this.selectionChange.emit(isSelected);
  }

  protected getAdviceSpecificStyling(advice: DailyAdvice): CssStyling {
    return advice.stylingOverride ?? getSprayTypeStyling(advice);
  }

  protected getClasses(advice: DailyAdvice | undefined): string[] {
    const classes: string[] = [];

    if (!advice) {
      return classes;
    }

    if (this.isToday(advice.date)) {
      classes.push('today');
    }

    if (this.isLastRow()) {
      classes.push('last-row');
    }

    return classes;
  }

  protected getAdviceText(advice: DailyAdvice): string {
    return advice.translationKey ?? getSprayTypeTranslation(advice, this.translationPrefix());
  }

  private isToday(date: Date): boolean {
    return this.dateService.isDateToday(date);
  }

  private getAdvices(dates: Date[], advice: CropFieldAdvice): DailyAdvice[] {
    return dates.map((date) => this.getAdvice(date, advice.advices));
  }

  private getAdvice(date: Date, advices: DailyAdvice[]): DailyAdvice {
    return advices.find((x) => this.dateService.isDateSameDay(x.date, date)) ?? {
      date,
      sprayAdvice: SprayAdvice.noAdvice,
      remainingFungicidePercentage: 0,
      isSprayedToday: false,
    };
  }
}
